import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { FullPageSpinner } from 'components/FullPageSpinner'
import { Button } from 'components'
import { useAnalytics } from 'hooks'
import { useEffect, useRef, useState } from 'react'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { PostModal } from '../components/PostModal'
import { useIdeaStore } from '../store/idea-store'
import { entrySourceFromLocationState } from '../utils/post-entry-source'
import { creativeHasRenderableMedia, isFailedSuggestion } from '../utils/suggestion-status'

/**
 * Arrival view for a single Post: deep-linkable /post/:id. This is where a
 * creation intent lands the instant the hero prompt is submitted — the post
 * is usually still generating, and the PostModal's preview pane narrates the
 * generation live (Pusher-driven via the global usePostPusher + the
 * topic-less refetch in setIdeaReady). Survives refresh: everything hydrates
 * from loadIdea. Design: playbook/product/home-creation-moment/DESIGN.md.
 */
export function PostArrivalPage() {
  const { id: projectId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { track } = useAnalytics()
  // Journey origin rides location.state across the hero → arrival hop (e.g.
  // 'byo_upload' for a bring-your-own-content create); default stays the
  // arrival surface itself.
  const entrySource = entrySourceFromLocationState(location.state) ?? 'post_arrival'
  const loadIdea = useIdeaStore(s => s.loadIdea)
  // Live-select from the store so Pusher-driven refreshes (ready content,
  // publish progress) re-render the modal without a manual reload.
  const idea = useIdeaStore(s => s.ideas.find(i => i.projectId === projectId) ?? null)
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'notfound'>('loading')
  const trackedViewRef = useRef(false)

  useEffect(() => {
    if (!projectId) return
    let cancelled = false
    setLoadState('loading')
    loadIdea(projectId)
      .then(result => {
        if (!cancelled) setLoadState(result ? 'ready' : 'notfound')
      })
      .catch(() => {
        if (!cancelled) setLoadState('notfound')
      })
    return () => {
      cancelled = true
    }
  }, [projectId, loadIdea])

  // Re-arm the view event when navigating between two /post/:id routes (the
  // component instance is reused, so a plain ref would only fire once ever).
  useEffect(() => {
    trackedViewRef.current = false
  }, [projectId])

  useEffect(() => {
    if (trackedViewRef.current || loadState !== 'ready' || !idea || !projectId) return
    trackedViewRef.current = true
    track(AnalyticsEvents.Home.HOME_ARRIVAL_VIEWED, {
      project_id: projectId,
      generating: !creativeHasRenderableMedia(idea.primaryCreative),
    })
  }, [loadState, idea, projectId, track])

  // Safety-net poll while the post is still generating: Pusher is the primary
  // signal, but a subscription established after the event fired (fresh tab,
  // flaky socket) would otherwise leave the arrival view generating forever.
  // Stops as soon as media lands or the generation terminally fails.
  const generating =
    loadState === 'ready' &&
    !!idea &&
    !creativeHasRenderableMedia(idea.primaryCreative) &&
    !isFailedSuggestion(idea.primaryCreative)
  useEffect(() => {
    if (!generating || !projectId) return
    const startedAt = Date.now()
    let tick = 0
    const interval = setInterval(() => {
      tick += 1
      // A hidden tab doesn't need the safety net — Pusher (plus the refetch
      // below on return) covers it without polling for a whole reel render.
      if (document.visibilityState !== 'visible') return
      // Past the first minute (a reel render), back off to ~32s: the poll is
      // a missed-Pusher safety net, not the primary signal.
      if (Date.now() - startedAt > 60_000 && tick % 4 !== 0) return
      void loadIdea(projectId).catch(err => console.log('Arrival safety poll failed:', err))
    }, 8000)
    return () => clearInterval(interval)
  }, [generating, projectId, loadIdea])

  // Long-open tab: media URLs are signed with a 6h TTL, so returning to a stale
  // tab refetches once for fresh links (mirrors the old Home focus behavior).
  useEffect(() => {
    if (!projectId) return
    const refetch = () => {
      if (document.visibilityState === 'visible')
        void loadIdea(projectId).catch(err => console.log('Arrival focus refetch failed:', err))
    }
    document.addEventListener('visibilitychange', refetch)
    return () => document.removeEventListener('visibilitychange', refetch)
  }, [projectId, loadIdea])

  if (!projectId) return <Navigate to="/" replace />

  if (loadState === 'loading') return <FullPageSpinner />

  if (loadState === 'notfound' || !idea) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-[17px] font-medium text-paper">We couldn't find this post.</p>
        <p className="max-w-sm text-[13.5px] text-paper-faint">
          It may have been deleted, or the link is from another business.
        </p>
        <Button variant="t3-tertiary" size="t3-sm" onClick={() => navigate('/')}>
          Back to home
        </Button>
      </div>
    )
  }

  return (
    <div className="h-full bg-bg">
      <PostModal
        idea={idea}
        openedFrom="post_arrival"
        entrySource={entrySource}
        onClose={() => navigate('/')}
      />
    </div>
  )
}
